﻿import {currentCulture, DateOnly, DateTime, DayOfWeek, NamedValue, TimeOnly} from "@2d-front-monorepo/core";
import {MvvmOptionGroupEntry} from "@2d-front-monorepo/design-library";
import {
	MultiOperationSegment,
	ParkingPrebookingAvailabilityEntry,
	RecurrenceType,
	SelectedParkingPrebookingAvailabilityEntry
} from "@2d-front-monorepo/permits-business";
import ParkingPrebookingAvailabilityType from "../enums/ParkingPrebookingAvailabilityType";
import {PayWith} from "../index";
import {TariffId} from "../nominalTypes/TariffId";
import {ZoneId} from "../nominalTypes/ZoneId";
import {GetParkingOperationStepsArguments} from "../types/arguments/GetParkingOperationStepsArguments";
import {isDateRangeValid} from "./isRangeValid";
import {MultiOperationPermit} from "./MultiOperationPermit";

type PrebookingTimes = {
	times: NamedValue<TimeOnly>[];
	available: boolean;
}

function isSameDay(date1: DateTime, date2: DateTime): boolean {
	return date1.toDateOnly().equals(date2.toDateOnly());
}

function createFullPrebookingTimes(): NamedValue<TimeOnly>[] {
	const culture = currentCulture();
	const result: NamedValue<TimeOnly>[] = [];
	for (let i = 0; i < 24; i++) {
		const time = TimeOnly.fromComponents(i, 0, 0);
		result.push({label: culture.formatTime(time, "short-time"), value: time});
	}

	return result;
}

function toDateMap(prebooking: ParkingPrebookingAvailabilityEntry[]): Map<string, PrebookingTimes> {
	const culture = currentCulture();
	const result = new Map<string, PrebookingTimes>();
	let index = -1;


	for (const entry of prebooking) {
		++index;
		let start = entry.startDate;
		const end = entry.endDate;
		const isAvailable = entry.type === ParkingPrebookingAvailabilityType.Available;

		while (start < end) {
			const key = start.toDateOnlyString();

			let times = result.get(key);
			if (times == null) {
				times = {times: [], available: isAvailable};
				result.set(key, times);
			} else {
				times.available ||= isAvailable;
			}

			if (isAvailable) {
				times.times.push({
					label: culture.formatTime(start.toTimeOnly(), "short-time"),
					value: start.toTimeOnly()
				});
			}

			start = start.addHours(1);
		}
	}

	return result;
}

function roundToHours(date: DateTime): TimeOnly {
	return TimeOnly.fromIsoString(date.getHours() + ":00:00");
}

function shouldDisable(day: DayOfWeek, sorted: DayOfWeek[], maxConsecutiveDays: number): boolean {
	if (sorted.length === 0) {
		return false;
	}

	const dayCount = 7;
	let maxStreak = 1;
	let currentStreak = 1;

	for (let i = 1; i < sorted.length; i++) {
		if (sorted[i] === (sorted[i - 1] + 1) || (sorted[i] === 0 && sorted[i - 1] === 6)) {
			currentStreak++;
			maxStreak = Math.max(maxStreak, currentStreak);
		} else {
			currentStreak = 1;
		}
	}

	const lastDayOfLongestStreak = sorted.reduce((prev, curr) => {
		if (curr === (prev + 1) || (curr === 0 && prev === 6)) {
			return curr;
		}
		return prev;
	}, sorted[0]);

	const nextDayAfterLongestStreak = (lastDayOfLongestStreak + 1) % dayCount;
	if (maxStreak >= maxConsecutiveDays && day === nextDayAfterLongestStreak) {
		return true;
	}

	const newSortedDays = [...sorted, day].sort((a, b) => a - b);
	let newStreak = 1;

	for (let i = 1; i < newSortedDays.length; i++) {
		if (newSortedDays[i] === (newSortedDays[i - 1] + 1) || (newSortedDays[i] === 0 && newSortedDays[i - 1] === 6)) {
			newStreak++;
			if (newStreak > maxConsecutiveDays) return true;
		} else {
			newStreak = 1;
		}
	}

	return false;
}

export type RecurrentPermitData = {
	availability: ParkingPrebookingAvailabilityEntry[],
	startDate: DateOnly,
	startTime: TimeOnly,
	endDate: DateOnly,
	endTime: TimeOnly,
	startRange: DateOnly,
	endRange: DateOnly,
	alwaysValidWeekdays: DayOfWeek[],
	maxConsecutiveDays: number,
	recurrence: RecurrenceType,
	daysOfWeek: DayOfWeek[]
}

type RecurrentPermitDataInternal = {
	data: RecurrentPermitData;
	operations: MultiOperationPermit;
	oldDaysOfWeek: DayOfWeek[] | undefined;
	oldEndDate: DateOnly;
	oldStartDate: DateOnly;
	maxRange: DateOnly;
	minRange: DateOnly;
	availableDates: Map<string, PrebookingTimes>;
	availableDaysOfWeek: MvvmOptionGroupEntry<DayOfWeek>[] | undefined;
	availableEndTimes: NamedValue<TimeOnly>[];
	availableStartTimes: NamedValue<TimeOnly>[];
	fullTimes: NamedValue<TimeOnly>[] | undefined;

}

export class RecurrentPermit {
	private static _map: WeakMap<RecurrentPermitData, RecurrentPermitDataInternal> = new WeakMap();

	public static dayClassName(data: RecurrentPermitData, value: DateOnly): string | undefined {
		const d = this._getInternalData(data);
		const e = d.availableDates.get(value.toJSON());
		if (e == null || d.data.alwaysValidWeekdays.includes(value.getDayOfWeek())) {
			return undefined;
		}

		return e.times.length === 24 ? undefined : "calendar__cell--holed";
	}

	public static empty(startDate: DateTime, endDate: DateTime, startRange: DateOnly, endRange: DateOnly, alwaysValidWeekdays: DayOfWeek[], maxConsecutiveDays: number): RecurrentPermitData {
		const data: RecurrentPermitData = {
			availability: [],
			startDate: startDate.toDateOnly(),
			startTime: roundToHours(startDate),
			endDate: endDate.toDateOnly(),
			endTime: roundToHours(endDate),
			startRange,
			endRange,
			alwaysValidWeekdays,
			maxConsecutiveDays,
			recurrence: "none",
			daysOfWeek: []
		};

		this._map.set(data, {
			data,
			operations: new MultiOperationPermit(data.availability),
			minRange: startRange,
			maxRange: endRange,
			oldDaysOfWeek: undefined,
			oldEndDate: data.endDate,
			oldStartDate: data.startDate,
			fullTimes: undefined,
			availableDates: new Map(),
			availableDaysOfWeek: undefined,
			availableEndTimes: [],
			availableStartTimes: []
		});

		return data;
	}

	public static getAvailableDaysOfWeek(data: RecurrentPermitData): MvvmOptionGroupEntry<DayOfWeek>[] {
		const d = this._getInternalData(data);
		if (d.oldDaysOfWeek === d.data.daysOfWeek && d.availableDaysOfWeek != null) {
			return d.availableDaysOfWeek;
		}

		d.oldDaysOfWeek = d.data.daysOfWeek;
		const dayNames = currentCulture().shortDayNames;
		const alwaysValidWeekdays = d.data.alwaysValidWeekdays;
		const sortedDays = [...d.data.daysOfWeek].sort((a, b) => a - b);

		let consecutiveDays = 1;
		let currentStreak = 1;

		for (let i = 1; i < sortedDays.length; i++) {
			if (sortedDays[i] === sortedDays[i - 1] + 1 || (sortedDays[i] === 0 && sortedDays[i - 1] === 6)) {
				currentStreak++;
				consecutiveDays = Math.max(consecutiveDays, currentStreak);
			} else {
				currentStreak = 1;
			}
		}

		const maxConsecutiveDays = d.data.maxConsecutiveDays;
		const disableRemaining = consecutiveDays >= maxConsecutiveDays;

		return d.availableDaysOfWeek = [
			{
				label: dayNames[1].label,
				value: DayOfWeek.Monday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Monday) || (disableRemaining && shouldDisable(DayOfWeek.Monday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[2].label,
				value: DayOfWeek.Tuesday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Tuesday) || (disableRemaining && shouldDisable(DayOfWeek.Tuesday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[3].label,
				value: DayOfWeek.Wednesday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Wednesday) || (disableRemaining && shouldDisable(DayOfWeek.Wednesday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[4].label,
				value: DayOfWeek.Thursday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Thursday) || (disableRemaining && shouldDisable(DayOfWeek.Thursday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[5].label,
				value: DayOfWeek.Friday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Friday) || (disableRemaining && shouldDisable(DayOfWeek.Friday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[6].label,
				value: DayOfWeek.Saturday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Saturday) || (disableRemaining && shouldDisable(DayOfWeek.Saturday, sortedDays, maxConsecutiveDays))
			},
			{
				label: dayNames[0].label,
				value: DayOfWeek.Sunday,
				disabled: alwaysValidWeekdays.includes(DayOfWeek.Sunday) || (disableRemaining && shouldDisable(DayOfWeek.Sunday, sortedDays, maxConsecutiveDays))
			}
		];
	}

	public static getAvailableEndTimes(data: RecurrentPermitData): NamedValue<TimeOnly>[] {
		const d = this._getInternalData(data);
		return d.data.recurrence === "none" ? d.availableEndTimes : this._getFullAvailableTimes(d);
	}

	public static getAvailableStartTimes(data: RecurrentPermitData): NamedValue<TimeOnly>[] {
		const d = this._getInternalData(data);
		return d.data.recurrence === "none" ? d.availableStartTimes : this._getFullAvailableTimes(d);
	}

	public static getDaysOfWeeks(data: RecurrentPermitData): string {
		const d = this._getInternalData(data);
		const dayNames = currentCulture().shortDayNames;
		const sortedDays = [...d.data.daysOfWeek].sort((a, b) => a - b);
		const result: string[] = [];
		for (const sortedDay of sortedDays) {
			result.push(dayNames[sortedDay].label);
		}

		return result.join(" - ");
	}

	public static getParkingOperationArguments(data: RecurrentPermitData, tariffId: TariffId, zoneId: ZoneId, plates: string[]): GetParkingOperationStepsArguments {
		const d = this._getInternalData(data);
		const selected = this.getSelected(data);
		return {
			startDate: selected[0].startDate,
			endDate: selected.at(-1)!.endDate,
			tariffId: tariffId,
			branch: {zoneId: zoneId},
			deferConfirmation: true,
			licensePlates: plates,
			payWith: PayWith.DebitCreditCard,
			preBookingEntries: d.data.availability,
			selectedPreBookingEntries: selected
		}
	}

	public static getSelected(data: RecurrentPermitData): SelectedParkingPrebookingAvailabilityEntry[] {
		const d = this._getInternalData(data);
		const result: SelectedParkingPrebookingAvailabilityEntry[] = [];

		for (const op of d.operations) {
			if (isSameDay(op.startDate, op.endDate)) {
				result.push(op.toPrebookingEntry());

			} else {
				result.push(...this._getSpans(op))
			}
		}

		return result;
	}

	public static isSelectableDateEnd(data: RecurrentPermitData, value: DateOnly): boolean {
		const d = this._getInternalData(data);
		if (this._isInvalid(d, value) || value.compareTo(d.data.startDate) < 0) {
			return false;
		}

		const e = d.availableDates.get(value.toJSON());
		if (e == null || !e.available) {
			return false;
		}

		if (!this._validateMaxConsecutiveDays(d, value)) {
			return false;
		}

		return isDateRangeValid(
			d.data.availability,
			DateTime.fromDateAndTime(d.data.startDate, d.data.startTime),
			DateTime.fromDateAndTime(value.addDays(1), TimeOnly.zero()),
			d.data.alwaysValidWeekdays
		);
	}

	public static isSelectableDateStart(data: RecurrentPermitData, value: DateOnly): boolean {
		const d = this._getInternalData(data);
		if (this._isInvalid(d, value)) {
			return false;
		}

		const e = d.availableDates.get(value.toJSON());
		return e == null ? false : e.available;
	}

	public static isValid(data: RecurrentPermitData): boolean {
		const d = this._getInternalData(data);
		if (d.data.startDate.compareTo(d.data.endDate) >= 0 && d.data.startTime.compareTo(d.data.endTime) >= 0) {
			return false;
		}

		if (d.data.recurrence === "none") {
			return d.operations.size === 1;
		} else {
			return d.operations.size > 1;
		}
	}

	public static isValidDateEnd(data: RecurrentPermitData): boolean {
		const value = data.endDate;
		const d = this._getInternalData(data);
		if (this._isInvalid(d, value)) {
			return false;
		}

		if (d.data.startDate.compareTo(value) > 0 && d.data.startTime.compareTo(d.data.endTime) >= 0) {
			return false;
		}

		return d.operations.size !== 0;
	}

	public static isValidDateStart(data: RecurrentPermitData): boolean {
		const value = data.startDate;
		const d = this._getInternalData(data);
		return !this._isInvalid(d, value);
	}

	public static isValidRangeEnd(data: RecurrentPermitData): boolean {
		const value = data.endRange;
		const d = this._getInternalData(data);
		if (this._isInvalid(d, value)) {
			return false;
		}

		if (d.data.startRange.compareTo(value) > 0 && d.data.startTime.compareTo(d.data.endTime) >= 0) {
			return false;
		}

		if (d.data.daysOfWeek.length === 0) {
			return true;
		}

		return d.operations.size > 1;
	}

	public static isValidTimeEnd(data: RecurrentPermitData): boolean {
		const value = data.endTime;
		const d = this._getInternalData(data);
		if (d.data.startDate.compareTo(d.data.endDate) >= 0 && d.data.startTime.compareTo(value) >= 0) {
			return false;
		}

		const endTimes = this.getAvailableEndTimes(data);
		return endTimes.find(x => x.value.equals(value)) != null;
	}

	public static isValidTimeStart(data: RecurrentPermitData): boolean {
		const value = data.startTime;
		const startTimes = this.getAvailableStartTimes(data);
		return startTimes.find(x => x.value.equals(value)) != null;
	}

	public static recalculate(data: RecurrentPermitData): void {
		this._recalculateAll(data);
	}

	public static update(data: RecurrentPermitData, availability: ParkingPrebookingAvailabilityEntry[]): void {
		const d = this._getInternalData(data);
		d.data.availability = availability;
		d.operations = new MultiOperationPermit(availability, data.alwaysValidWeekdays);
		d.availableDates = toDateMap(availability);
		d.oldEndDate = DateOnly.minValue();

		const nextSlot = this._getNextAvailableSlot(d, DateTime.localNow(), 1);
		if (nextSlot) {
			d.data.startDate = nextSlot.toDateOnly();
			d.data.startTime = nextSlot.toTimeOnly();
			d.data.endDate = nextSlot.addHours(1).toDateOnly();
			d.data.endTime = nextSlot.addHours(1).toTimeOnly();
		}

		this._recalculateAll(data, true);
	}

	private static _getAvailableTimes(data: RecurrentPermitDataInternal, value: DateOnly): NamedValue<TimeOnly>[] {
		const key = value.toJSON();
		const prebookingTimes = data.availableDates.get(key);
		return prebookingTimes ? prebookingTimes.times : [];
	}

	private static _getFullAvailableTimes(data: RecurrentPermitDataInternal): NamedValue<TimeOnly>[] {
		return data.fullTimes ??= createFullPrebookingTimes();
	}

	private static _getInternalData(data: RecurrentPermitData): RecurrentPermitDataInternal {
		return this._map.get(data)!;
	}

	private static _getNextAvailableSlot(data: RecurrentPermitDataInternal, startDate: DateTime, numberOfHours: number): DateTime | undefined {
		const operations = data.operations.availability;

		if (operations.length === 0) {
			return undefined;
		}

		const filtered = operations.filter(x => x.endDate > startDate && x.type === ParkingPrebookingAvailabilityType.Available);

		let index = 0;
		let startingDate = DateTime.fromDateAndTime(startDate.toDateOnly(), roundToHours(startDate).addHours(1));

		while (index < filtered.length) {
			const operation = filtered[index++];
			const endDate = startingDate.addHours(numberOfHours);

			if (endDate <= operation.endDate) {
				if (startingDate >= operation.startDate) {
					return startingDate;
				}

				return operation.startDate;

			} else {
				startingDate = filtered[index].startDate;
			}
		}

		return undefined;
	}

	private static _getSpans(op: MultiOperationSegment): SelectedParkingPrebookingAvailabilityEntry[] {
		const spans: SelectedParkingPrebookingAvailabilityEntry[] = [];

		let currentStart = op.startDate;
		const endDate = op.endDate;
		const firstSpanEnd = op.startDate.startOfDay().addDays(1);

		//first span
		spans.push({
			startDate: currentStart,
			endDate: firstSpanEnd
		})

		currentStart = firstSpanEnd;
		while (currentStart < endDate) {
			//complete days
			const spanEnd = currentStart.addDays(1);
			if (spanEnd > endDate) break;

			spans.push({
				startDate: currentStart,
				endDate: spanEnd
			});

			currentStart = spanEnd;
		}

		//last span (if any)
		if (currentStart < endDate) {
			spans.push({
				startDate: currentStart,
				endDate: endDate
			});
		}

		return spans;
	}

	private static _isInvalid(data: RecurrentPermitDataInternal, value: DateOnly): boolean {
		const invalid = value < data.minRange || value > data.maxRange;
		if (data.data.recurrence === "none") {
			return invalid || value.getDayOfWeek() == DayOfWeek.Sunday;
		}

		return invalid;
	}

	private static _recalculateAll(data: RecurrentPermitData, force: boolean = false): void {
		const d = this._getInternalData(data);
		const operations = d.operations;
		operations.clear();
		d.availableStartTimes = this._recalculateAvailableStartTimes(d, force);
		d.availableEndTimes = this._recalculateAvailableEndTimes(d, force);

		switch (d.data.recurrence) {
			case "none":
				this._recalculateNone(d);
				return;

			case "weekly":
				this._recalculateWeekly(d);
				break;

			default:
				throw new Error("Unknown recurrence type");
		}
	}

	private static _recalculateAvailableEndTimes(data: RecurrentPermitDataInternal, force: boolean): NamedValue<TimeOnly>[] {
		if (data.oldEndDate.equals(data.data.endDate) && !force) {
			return data.availableEndTimes;
		}

		const available = this._getAvailableTimes(data, data.data.endDate);
		if (available.length === 0) {
			return [];
		}

		const result: NamedValue<TimeOnly>[] = [];
		let startHour = 0;

		if (data.data.startDate.equals(data.data.endDate)) {
			startHour = data.data.startTime.getHours() + 1;
		}

		//first I find the index of the startHour. Can't use the hour as index because there could be missing entries before this time.
		const startIndex = available.findIndex(x => x.value.getHours() === startHour);
		for (let i = startIndex; i < available.length; ++i) {
			const current = available[i];
			if (!current.value.equals(TimeOnly.fromComponents(startHour++, 0, 0))) {
				break;
			}

			result.push(current);
		}

		return result;
	}

	private static _recalculateAvailableStartTimes(data: RecurrentPermitDataInternal, force: boolean): NamedValue<TimeOnly>[] {
		if (data.oldStartDate.equals(data.data.startDate) && !force) {
			return data.availableStartTimes;
		}

		const available = this._getAvailableTimes(data, data.data.startDate);
		if (available.length === 0) {
			return [];
		}

		const result: NamedValue<TimeOnly>[] = [];
		let startHour = 0;
		const today = DateTime.localNow();

		if (data.data.startDate.equals(today.toDateOnly())) {
			startHour = today.getHours() + 1;

		} else {
			return available;
		}

		//first I find the index of the startHour. Can't use the hour as index because there could be missing entries before this time.
		const startIndex = available.findIndex(x => x.value.getHours() >= startHour);
		for (let i = startIndex; i < available.length; ++i) {
			result.push(available[i]);
		}

		return result;
	}

	private static _recalculateNone(data: RecurrentPermitDataInternal): void {
		const startDate = DateTime.fromDateAndTime(data.data.startDate, data.data.startTime);
		const endDate = DateTime.fromDateAndTime(data.data.endDate, data.data.endTime);
		data.operations.tryCreateSegment(startDate, endDate);
	}

	private static _recalculateWeekly(data: RecurrentPermitDataInternal): void {
		const daysOfWeek = data.data.daysOfWeek;
		if (daysOfWeek.length === 0) {
			return;
		}

		let recurrenceStartDate = data.data.startRange;
		const recurrenceEndDate = data.data.endRange;
		const operations = data.operations;

		while (recurrenceStartDate < recurrenceEndDate) {
			if (daysOfWeek.includes(recurrenceStartDate.getDayOfWeek())) {
				const rs = DateTime.fromDateAndTime(recurrenceStartDate, data.data.startTime);
				const re = DateTime.fromDateAndTime(recurrenceStartDate, data.data.endTime);

				if (!operations.tryCreateSegment(rs, re)) {
					operations.clear();
					return;
				}
			}

			recurrenceStartDate = recurrenceStartDate.addDays(1);
		}
	}

	private static _validateMaxConsecutiveDays(data: RecurrentPermitDataInternal, endDate: DateOnly): boolean {
		if (data.data.maxConsecutiveDays <= 0) {
			return true;
		}

		let dateCount = 0;
		for (let currentDate = data.data.startDate; currentDate < endDate; currentDate = currentDate.addDays(1)) {
			if (currentDate.getDayOfWeek() == DayOfWeek.Sunday) {
				continue;
			}

			const e = data.availableDates.get(currentDate.toJSON());
			if (e == null || !e.available) {
				return false;
			}

			++dateCount;
		}

		return dateCount < data.data.maxConsecutiveDays;
	}
}
